import React from 'react';
import Grid from '@mui/material/Grid'; // Grid2 in mui 6
import { Alert, TextField, Autocomplete, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// variant types
// 1. basicList - account number, routing number, and account name with optional confirm account number
// 2. basicListTight - same as basicList but tighter spacing
// 3. detailedList - same as basicList but additional fields: bank name and bank account type
// 4. detailedBlock - same as detailedList but in condensed block format
// 5. dropDown - same as detailedList but uses dropdown for bank account type
const achFormVariantTypes = {
  basicList: 'basicList',
  basicListTight: 'basicListTight',
  detailedList: 'detailedList',
  detailedBlock: 'detailedBlock',
};

// disabling
// - disable === true will disable all inputs
// - if ach has a field called maskedValue will disable masked value and routing number by default
// - otherwise all enabled

const achFormAccountTypes = [
  { value: 'Business Checking', label: 'Business Checking' },
  { value: 'Business Savings', label: 'Business Savings' },
  { value: 'Personal Checking', label: 'Personal Checking' },
  { value: 'Personal Savings', label: 'Personal Savings' },
];

const ACHForm = React.forwardRef(function ACHForm(props, ref) {
  const { ach, handleChangeACH, location, disabled = false, variant = achFormVariantTypes.basicList } = props;
  const { t } = useTranslation();
  const [confirmAccountNumber, setConfirmAccountNumber] = React.useState('');
  const [achError, setAchError] = React.useState({
    accountName: false,
    accountNumber: false,
    confirmAccountNumber: false,
    routingNumber: false,
    accountType: false,
    bankName: false,
  });
  const [isValidConfirmAccountNumber, setIsValidConfirmAccountNumber] = React.useState(true);
  const [isValidRoutingNumberLength, setIsValidRoutingNumberLength] = React.useState(true);
  const [pasteAttempted, setPasteAttempted] = React.useState(false);
  const [pasteDisabled, setPasteDisabled] = React.useState(true);

  const numbersOnly = /^\d*$/;
  const isMasked = !!ach?.maskedValue;
  const requireConfirmAccountNumber =
    (location?.confirmAccountNumber || variant === achFormVariantTypes.detailedBlock) && !(isMasked || disabled);

  React.useImperativeHandle(ref, () => {
    return {
      validate: () => {
        let tempAchError = {
          accountName: false,
          accountNumber: false,
          confirmAccountNumber: false,
          routingNumber: false,
          accountType: false,
          bankName: false,
        };
        let isValid = true;
        if (!isMasked) {
          if (requireConfirmAccountNumber && ach.accountNumber !== confirmAccountNumber) {
            isValid = false;
            tempAchError.confirmAccountNumber = true;
          }
          if (!ach.accountNumber || (typeof ach.accountNumber === 'string' && ach.accountNumber.trim() === '')) {
            isValid = false;
            tempAchError.accountNumber = true;
          }
          if (
            !ach.routingNumber ||
            (typeof ach.routingNumber === 'string' && ach.routingNumber.trim() === '') ||
            ach.routingNumber?.length !== 9
          ) {
            isValid = false;
            tempAchError.routingNumber = true;
          }
          setIsValidRoutingNumberLength(!ach.routingNumber || ach.routingNumber?.length === 9);
        }

        if (!ach.accountName || (typeof ach.accountName === 'string' && ach.accountName.trim() === '')) {
          isValid = false;
          tempAchError.accountName = true;
        }

        // add other validation for new values depending on variant
        if (variant === achFormVariantTypes.detailedBlock || variant === achFormVariantTypes.detailedList) {
          if (!ach.accountType || (typeof ach.accountType === 'string' && ach.accountType.trim() === '')) {
            isValid = false;
            tempAchError.accountType = true;
          }
          if (!ach.bankName || (typeof ach.bankName === 'string' && ach.bankName.trim() === '')) {
            isValid = false;
            tempAchError.bankName = true;
          }
        }
        setAchError(tempAchError);
        return isValid;
      },
    };
  });

  const handleChangeAccountNum = (e) => {
    const newValue = e.target?.value;

    if (newValue.match(numbersOnly)) {
      handleChangeACH(e);

      if (requireConfirmAccountNumber) {
        if (e.nativeEvent?.inputType === 'insertFromPaste') {
          setPasteDisabled(false); // if account number pasted, confirm account number can be pasted for new browsers
        } else {
          setPasteDisabled(true); // if any typing then disable paste again
        }
        if (confirmAccountNumber === newValue) {
          setIsValidConfirmAccountNumber(true);
        } else {
          setIsValidConfirmAccountNumber(false);
        }
      }
    }
  };

  const handleChangeConfirmAccountNum = (e) => {
    const newValue = e.target?.value;
    if (newValue.match(numbersOnly)) {
      setConfirmAccountNumber(newValue);

      if (requireConfirmAccountNumber) {
        if (pasteAttempted) setPasteAttempted(false);
        if (ach.accountNumber === newValue) {
          setIsValidConfirmAccountNumber(true);
        } else {
          setIsValidConfirmAccountNumber(false);
        }
      }
    }
  };

  const handleChangeDropDown = (e, val) => {
    let event = e;
    Object.assign(event, { target: { ...event.target, id: 'accountType', value: val.value } });
    handleChangeACH(event);
  };

  const handleConfirmPaste = (e) => {
    if (pasteDisabled) {
      e.preventDefault();
      setPasteAttempted(true);
    }
  };

  const renderAccountName = (size = 12) => {
    return (
      <Grid item xs={12} md={size}>
        <Tooltip title="Max 30 characters." placement="top-start">
          <TextField
            required
            error={achError.accountName}
            disabled={disabled}
            id="accountName"
            type="text"
            label={t('AccountName')}
            value={ach?.accountName}
            fullWidth
            inputProps={{ maxLength: 30 }}
            onChange={(e) => handleChangeACH(e)}
            variant="outlined"
          />
        </Tooltip>
      </Grid>
    );
  };

  const renderAccountNumber = (size = 12) => {
    return (
      <Grid item xs={12} md={size}>
        <TextField
          required
          error={achError.accountNumber}
          disabled={isMasked || disabled}
          id="accountNumber"
          type="text"
          label={t('AccountNumber')}
          value={isMasked ? ach?.maskedValue || '****' : ach?.accountNumber}
          fullWidth
          onChange={(e) => handleChangeAccountNum(e)}
          variant="outlined"
        />
      </Grid>
    );
  };

  const renderConfirmAccountNumber = (size = 12) => {
    return (
      <Grid item xs={12} md={size}>
        <TextField
          required
          error={achError.confirmAccountNumber}
          disabled={isMasked || disabled}
          id="confirmAccountNumber"
          type="text"
          label={t('ConfirmAccountNumber')}
          value={isMasked ? ach?.maskedValue || '****' : confirmAccountNumber}
          fullWidth
          onChange={(e) => handleChangeConfirmAccountNum(e)}
          variant="outlined"
          inputProps={{
            onPaste: (e) => {
              handleConfirmPaste(e);
            },
          }}
        />
      </Grid>
    );
  };

  const renderRoutingNumber = (size = 12) => {
    return (
      <Grid item xs={12} md={size}>
        <TextField
          required
          error={achError.routingNumber}
          disabled={isMasked || disabled}
          id="routingNumber"
          type="text"
          label={t('RoutingNumber')}
          value={isMasked ? ach?.routingNumber?.slice(-4) || '****' : ach?.routingNumber}
          onChange={(e) => {
            if (e.target.value.match(numbersOnly)) {
              handleChangeACH(e);
            }
          }}
          variant="outlined"
          fullWidth
        />
      </Grid>
    );
  };

  const renderBankAccountType = (size = 12) => {
    return (
      <Grid item xs={12} md={size}>
        <Autocomplete
          id="accountType"
          disableClearable
          disabled={disabled}
          options={achFormAccountTypes}
          value={ach?.accountType}
          renderInput={(params) => (
            <TextField required error={achError.accountType} {...params} label={t('BankAccountType')} />
          )}
          onChange={(e, val) => handleChangeDropDown(e, val)}
          isOptionEqualToValue={(option, value) => !value?.value || option?.value === value?.value}
        />
      </Grid>
    );
  };

  const renderBankName = (size = 12) => {
    return (
      <Grid item xs={12} md={size}>
        <TextField
          required
          error={achError.bankName}
          disabled={disabled}
          id="bankName"
          type="text"
          label={t('BankName')}
          value={ach?.bankName}
          fullWidth
          onChange={(e) => handleChangeACH(e)}
          variant="outlined"
        />
      </Grid>
    );
  };

  const renderErrorMessages = (size = 12) => {
    return (
      <>
        {pasteAttempted && (
          <Grid item xs={12} md={size}>
            <Alert severity="error">Paste is disabled. Please type in your account number to confirm.</Alert>
          </Grid>
        )}
        {!isValidConfirmAccountNumber && (
          <Grid item xs={12} md={size}>
            <Alert severity="error">Account numbers do not match.</Alert>
          </Grid>
        )}
        {!isValidRoutingNumberLength && (
          <Grid item xs={12} md={size}>
            <Alert severity="error">Routing number should be 9 digits.</Alert>
          </Grid>
        )}
      </>
    );
  };

  const renderBasicList = () => {
    let spacing = 3;
    if (variant === achFormVariantTypes.basicListTight) {
      spacing = 2;
    }
    return (
      <Grid container rowSpacing={spacing}>
        {renderAccountName()}
        {renderAccountNumber()}
        {requireConfirmAccountNumber && renderConfirmAccountNumber()}
        {renderRoutingNumber()}
        {renderErrorMessages()}
      </Grid>
    );
  };

  const renderDetailedList = () => {
    return (
      <Grid container rowSpacing={2} columnSpacing={2}>
        {renderAccountName()}
        {renderAccountNumber()}
        {requireConfirmAccountNumber && renderConfirmAccountNumber()}
        {renderRoutingNumber()}
        {renderBankAccountType()}
        {renderBankName()}
        {renderErrorMessages()}
      </Grid>
    );
  };

  const renderDetailedBlock = () => {
    return (
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid item container rowSpacing={2} columnSpacing={2} justifyContent={'space-between'}>
          {renderAccountNumber(true)}
          {requireConfirmAccountNumber && renderConfirmAccountNumber(6)}
        </Grid>
        <Grid item container rowSpacing={2} columnSpacing={2} justifyContent={'space-between'}>
          {renderRoutingNumber(6)}
          {renderBankAccountType(6)}
        </Grid>
        {renderAccountName(12)}
        {renderBankName()}
        {renderErrorMessages()}
      </Grid>
    );
  };

  switch (variant) {
    case achFormVariantTypes.basicList:
      return renderBasicList();
    case achFormVariantTypes.basicListTight:
      return renderBasicList();
    case achFormVariantTypes.detailedList:
      return renderDetailedList();
    case achFormVariantTypes.detailedBlock:
      return renderDetailedBlock();
    default:
      return renderBasicList();
  }
});

ACHForm.propTypes = {
  ach: PropTypes.shape({
    accountName: PropTypes.string,
    accountNumber: PropTypes.string,
    routingNumber: PropTypes.string,
    bankName: PropTypes.string,
    accountType: PropTypes.string,
  }),
  handleChangeACH: PropTypes.any,
  location: PropTypes.object,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
};

export default ACHForm;
